import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { BetaApplication } from '../../types';
import { ClipboardList, Clock, CheckCircle, XCircle, BookOpen, AlertCircle, FileText } from 'lucide-react';
import { useBetaAssignments, useAssignmentFeedback } from '../../hooks/useBetaAssignments';
import AssignmentList from '../../components/beta/assignments/AssignmentList';
import AssignmentView from '../../components/beta/assignments/AssignmentView';

interface ReaderDashboardProps {
  user: User;
}

export default function ReaderDashboard({ user }: ReaderDashboardProps) {
  const [applications, setApplications] = useState<BetaApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const { assignments, updateAssignmentStatus, refresh } = useBetaAssignments(undefined, user.id);
  const { feedback, addFeedback } = useAssignmentFeedback(selectedAssignment?.id || '');

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load user's applications
        const { data: applicationsData, error: applicationsError } = await supabase
          .from('beta_reader_applications')
          .select(`
            *,
            book_ideas!beta_reader_applications_idea_id_fkey (
              title,
              description
            ),
            author:profiles!beta_reader_applications_author_id_fkey (
              full_name
            )
          `)
          .eq('reader_id', user.id)
          .order('created_at', { ascending: false });

        if (applicationsError) throw applicationsError;
        setApplications(applicationsData || []);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Beta Reader Dashboard</h1>

        {/* Development Notice */}
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Beta Features Under Development</h3>
              <p className="text-blue-700 text-sm mt-1">
                The beta reading features are currently in active development. We're working on adding more functionality 
                like chapter assignments, feedback tools, and direct communication with authors. Stay tuned for updates!
              </p>
            </div>
          </div>
        </div>

        {/* Assignments Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 bg-gradient-to-r from-violet-100 to-violet-50 border-b">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold text-violet-900">My Assignments</h2>
            </div>
          </div>
          
          <div className="p-6">
            {selectedAssignment ? (
              <AssignmentView
                assignment={selectedAssignment}
                feedback={feedback}
                isAuthor={false}
                onBack={() => setSelectedAssignment(null)}
                onUpdateStatus={async (status) => {
                  await updateAssignmentStatus(selectedAssignment.id, status);
                  await refresh();
                }}
                onSubmitFeedback={addFeedback}
              />
            ) : (
              <AssignmentList
                assignments={assignments}
                isAuthor={false}
                onSelect={setSelectedAssignment}
              />
            )}
          </div>
        </div>

        {/* Applications Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-violet-100 to-violet-50 border-b">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold text-violet-900">My Applications</h2>
            </div>
          </div>
          
          <div className="divide-y">
            {applications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>You haven't applied to beta read any books yet.</p>
                <p className="text-sm mt-1">
                  Visit book ideas to find stories you'd like to help shape!
                </p>
              </div>
            ) : (
              applications.map(application => (
                <div key={application.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">
                        {application.book_ideas.title}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        Author: {application.author.full_name}
                      </p>
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
                        application.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : application.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {application.status === 'approved' ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : application.status === 'rejected' ? (
                          <XCircle className="w-4 h-4" />
                        ) : (
                          <Clock className="w-4 h-4" />
                        )}
                        <span className="font-medium">
                          {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}